/*
 * sennder custom catalog index.
 *
 * Forked from:
 * https://github.com/backstage/backstage/blob/master/plugins/catalog/src/components/CatalogPage/DefaultCatalogPage.tsx
 *
 * As recommended by:
 * https://backstage.io/docs/features/software-catalog/catalog-customization/
 *
 */

import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton
} from '@backstage/core-components'
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api'
import {
  CatalogFilterLayout,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker
} from '@backstage/plugin-catalog-react'
import { catalogPlugin, DefaultCatalogPageProps } from '@backstage/plugin-catalog'
import { CustomCatalogTable } from './CustomCatalogTable'
import { EntityFailedChecksPicker } from './EntityFailedChecksPicker'
import { EntityPassedChecksPicker } from './EntityPassedChecksPicker'
import { EntityBaPodPicker } from './EntityBaPodPicker'
import { EntityArchivalStatePicker } from './EntityArchivalStatePicker'
import { EntityExecutionModePicker } from './EntityExecutionModePicker'
import { EntityServiceTypePicker } from './EntityServiceTypePicker'
import { EntityLibraryTypePicker } from './EntityLibraryTypePicker'
import { EntityQueueTypePicker } from './EntityQueueTypePicker'
import { EntityDatabaseTypePicker } from './EntityDatabaseTypePicker'
import { EntityRuntimeVersionPicker } from './EntityRuntimePicker'
import { CustomEntityKindPicker } from './CustomEntityKindPicker'

export function CustomCatalogIndexPage(props: DefaultCatalogPageProps) {
  const {
    columns,
    actions,
    initiallySelectedFilter = 'all',
    initialKind = 'entity',
    tableOptions = {},
    emptyContent
  } = props

  const defaultTableOptions = {
    pageSize: 50,
    pageSizeOptions: [20, 50, 100, 200, 500],
    ...tableOptions
  }
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage'
  const createComponentLink = useRouteRef(catalogPlugin.externalRoutes.createComponent)

  return (
    <PageWithHeader title={`${orgName} Catalog`} themeId="home">
      <Content>
        <ContentHeader title="">
          <CreateButton
            title="+ Component"
            to={createComponentLink && createComponentLink()}
          />
          <SupportButton>All your software catalog entities</SupportButton>
        </ContentHeader>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <CustomEntityKindPicker initialFilter={initialKind} />
              <EntityTypePicker />
              <EntityArchivalStatePicker initialFilter="non-archived" />
              <EntityBaPodPicker />
              <UserListPicker initialFilter={initiallySelectedFilter} />
              <EntityRuntimeVersionPicker />
              <EntityServiceTypePicker />
              <EntityLibraryTypePicker />
              <EntityExecutionModePicker />
              <EntityDatabaseTypePicker />
              <EntityQueueTypePicker />
              <EntityFailedChecksPicker />
              <EntityPassedChecksPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CustomCatalogTable
                columns={columns}
                actions={actions}
                tableOptions={defaultTableOptions}
                emptyContent={emptyContent}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  )
}
